import { all, call, fork, put, take } from 'redux-saga/effects';
import Nprogress from 'nprogress';

import userProfileSagas from './userSagas';
import projectSagas from './projectSagas';
import serviceSagas from './serviceSagas';
import clientSagas from './clientSagas';
import credentialSagas from './credentialSagas';
import rbacSagas from './rbacSagas';
import taskSagas from './taskSagas';
import articleSagas from './articleSagas';
import eventSagas from './eventSagas';
import commentSagas from './commentSagas';
import legalEntitySagas from './legalEntitySagas';
import notificationSagas from './notificationSagas';
import activityLogSagas from './activityLogSagas';
import companySagas from './companySagas';
import salaryHistorySagas from './salaryHistorySagas';
import searchSagas from './searchSagas';

import { APP_STARTUP, SIGNIN_USER_SUCCESS } from 'constants/ActionTypes';
import { fetchResource, getIntlMessage, showErrorMessage, LOGOUT_ACTION_TYPES } from 'helpers/utilHelpers';
import { loadModerateCount, loadUserLastTask } from 'actions/entities/userActions';
import { setAuthData, setAuthUser } from 'actions/authActions';
import { appReady } from 'actions/entities/entitiesActions';
import { history } from 'store';
import { deleteCookie } from 'helpers/cookieHelpers';
import { AUTH_COOKIE } from 'constants/UtilConstants';
import SocketService from 'services/SocketService';
import loadEntitiesSaga from '../util/loadEntitiesSaga';
import startCheckEntitiesUpdatedSaga from '../util/startCheckEntitiesUpdatedSaga';
import clearSavedData from '../../util/clearSavedData';
import initializeSocketSaga from 'sagas/util/initializeSocketSaga';
import workspaceSaga from './workspaceSagas';
import { setCompanyLanguage } from 'helpers/companyHelpers';
import { aliasHeader } from 'util/config';
import { THEME_TYPE_LITE } from 'constants/ThemeSetting';
import { setDarkTheme, setLightTheme } from 'actions/ui/appActions';
import redirectManager from '../../util/redirect-manager.js';

const socketInstance = SocketService.Instance;
let companyName = null;

function* loadApp(action) {
  try {
    const { alias } = action;
    const headers = alias ? { [aliasHeader]: alias } : null;

    // First of all loads info about authorized user
    const authUser = yield call(fetchResource, 'workspaces/profile', 'GET', null, headers, {
      expand: ['state', 'companyEntities', 'companySettings', 'showReportPage', 'companyStatus', 'companyAlias'],
    });

    if (authUser.companySettings?.enabledRedirects) {
      redirectManager(authUser.companyAlias);
    }

    const theme = authUser.state.theme;
    if (theme) {
      if (theme === THEME_TYPE_LITE) {
        yield put(setLightTheme());
      } else {
        yield put(setDarkTheme());
      }
    }

    const settings = authUser.companySettings;
    const { language, name } = settings;
    companyName = name;
    setCompanyLanguage(language, authUser.companyAlias);

    yield put(setAuthUser(authUser));

    Nprogress.set(0.4);

    yield call(loadEntitiesSaga, true);

    Nprogress.set(0.7);

    Nprogress.done();

    yield put(appReady());

    yield call(initializeSocketSaga, authUser.userId, authUser.companyId);

    yield fork(startCheckEntitiesUpdatedSaga);

    if (authUser.userType !== 'client') {
      yield put(loadUserLastTask());
      yield put(loadModerateCount());
    }

    yield take(LOGOUT_ACTION_TYPES);
    yield call(socketInstance.disconnect);
    yield call(clearSavedData);
  } catch (error) {
    if (error.status === 401) {
      // Remove inactive token
      deleteCookie(AUTH_COOKIE);
      localStorage.removeItem('authToken');
      yield put(setAuthData(null));
      // Disconnect socket if it is open
      yield call(socketInstance.disconnect);
      // Go to the signin page
      history.push('/signin');
      yield call(clearSavedData);
      // Set Up progress as done
      Nprogress.done();
    } else {
      yield showErrorMessage(getIntlMessage('fetch.appStartup'));
    }
  }
}

function* applicationStartup() {
  while (true) {
    const action = yield take(APP_STARTUP);
    Nprogress.start();
    // Perform initial info load
    yield call(loadApp, action);
  }
}

function* clearPersistAfterLogin() {
  while (true) {
    yield take(SIGNIN_USER_SUCCESS);
    yield call(clearSavedData);
  }
}

// Function return application title
export const appTitle = () => (companyName ? companyName : '');

export default function* rootSaga() {
  yield all([
    fork(applicationStartup),
    fork(clearPersistAfterLogin),
    userProfileSagas(),
    projectSagas(),
    serviceSagas(),
    clientSagas(),
    credentialSagas(),
    rbacSagas(),
    taskSagas(),
    articleSagas(),
    eventSagas(),
    commentSagas(),
    legalEntitySagas(),
    notificationSagas(),
    activityLogSagas(),
    companySagas(),
    salaryHistorySagas(),
    searchSagas(),
    workspaceSaga(),
  ]);
}
